import React from 'react';
import PropTypes from 'prop-types';
import { withRouter } from 'next/router';
import { connect } from 'react-redux';
import Head from 'next/head';
import DonationForm from '../DonationForm';
import { DONATION_FORM_STEPS, SD, RG, DEFAULT_COUNTRY_CODE } from '../../../utils/constants';
import {
  buildStepPath,
  getFundraisingCostsExplanation,
  getGiftAidAmount,
  isFundraisingCostsVisible,
  normalisePreferencesValues,
} from '../../../utils/donationForm';
import { hasShortformValuesInQuery } from '../../../utils/shortform';
import { getDonationFormFooterSettingsSelector } from '../../../utils/selectors';
import {
  pushCheckoutStep as gtmPushCheckoutStep,
  pushCheckoutOption as gtmPushCheckoutOption,
  pushPurchase as gtmPushPurchase,
} from '../../../utils/gtm';
import Header from '../Header';
import DonationFormFooter from '../DonationFormGenerics/DonationFormFooter';
import HeroImageWithShapedBackground from '../BodyBlocks/BBHeroWithMoneyHandles/HeroImageWithShapedBackground';
import { sessionStorageSetItem } from '../../../utils/web-storage';

const debug = require('debug')('cw:DonationFormPage');

import heroStyles from '../BodyBlocks/BBHeroWithMoneyHandles/index.module.scss';

/**
 * Wrapper around donation form wizard.
 * Adds path URLs (mostly for analytics purposes) to each donation form step
 * using shallow routing (no page loading).
 */
class DonationFormPage extends React.Component {
  constructor(props) {
    super(props);

    this.onStepChange = this.onStepChange.bind(this);
  }

  componentDidMount() {
    document.body.classList.add('donation-page-opened');
    const { formDonationType } = this.props;
    // formDonationType doesn't exist with SSR, therefore we call onStepChange only
    // with client rendering to fire 'checkout' event in GTM.
    // formDonationType will be automatically updated with SSR, therefore onStepChange
    // method will be executed in the componentDidUpdate.
    if (formDonationType) {
      this.onStepChange({ name: 'amount', index: 0 });
    }
  }

  componentDidUpdate(prevProps) {
    const {
      completed = false,
      orderID = 0,
      formDonationType,
      formConfig,
      formStepName,
      formStepIndex,
      formValues = {},
    } = this.props;

    // The store informed about successful donation. Redirect to Thank You.
    if (prevProps.completed === false && completed === true && orderID > 0) {
      if (DEFAULT_COUNTRY_CODE === 'GB' && 'giftaid' in formValues && !formValues.giftaid) {
        // For UK region we want to show giftaid form on Thank you page.
        // Also, we want to have expiration time for this form, so
        // we need to know a time when the order was created.
        const currentTime = +new Date();
        window.sessionStorage.setItem(`cw_order_${orderID}`, `${currentTime}:false`);
      }

      // Clear the donation attempt URL so the ReturnToDonationPopup is not displayed.
      window.sessionStorage.removeItem('donationFormAttempt');

      this.redirectToThankYouPage();

      const formContactPreferences = formValues.contact_preferences;
      const viewedPreferences = formValues.viewedPreferencesOptions;
      const contactPreferences = normalisePreferencesValues(
        formContactPreferences,
        viewedPreferences,
      );

      const amount = formValues.fundraisingCostsApplied
        ? formValues.totalAmount
        : formValues.amount;
      // Send purchase event to GTM.
      const transactionData = {
        id: orderID,
        revenue: amount,
      };

      const isFundraisingCostsAsked =
        formValues.show_fundraising_costs && isFundraisingCostsVisible(formValues);

      const isCompanyDonation =
        formValues.company_donation && formValues.company_donation[0] === 'company_donation';
      const isCompanyDonationAsked = formValues.show_company_donation;

      const products = [
        {
          name: formConfig.title,
          id: formValues.appealId,
          price: amount,
          category:
            formValues.type === 'recurring_donation' ? 'Monthly donation' : 'Single donation',
          variant: formValues.type,
          quantity: 1,
          contact_preferences: contactPreferences,
          source_code: formValues.source_code || '',
          fundraising_costs_applied: isFundraisingCostsAsked
            ? formValues.fundraisingCostsApplied
            : 'not_asked',
          fundraising_costs_amount: formValues.fundraisingCostsAmount,
          fundraising_costs_formula: getFundraisingCostsExplanation(
            isFundraisingCostsAsked ? formValues.fundraisingCostsFormula : 'not_asked',
          ),
          fundraising_costs_element_type: isFundraisingCostsAsked ? 'dropdown' : 'not_asked',
          company_donation: isCompanyDonationAsked ? isCompanyDonation : 'not_asked',
          gift_aid: formValues.giftaid ? '1' : '0',
          gift_aid_amount: getGiftAidAmount(amount, formValues.giftaid),
        },
      ];
      gtmPushPurchase(transactionData, products);
    }

    // The first form step should change URL based on chosen donation type.
    if (formDonationType && prevProps.formDonationType !== formDonationType) {
      this.onStepChange({ name: 'amount', index: 0 });
      return;
    }

    if (formStepName && formDonationType && prevProps.formStepName !== formStepName) {
      this.onStepChange({ name: formStepName, index: formStepIndex });
    }

    // We should send updated data to GTM if user changed payment method.
    if (prevProps.formValues.payment_method !== formValues.payment_method) {
      gtmPushCheckoutOption(DONATION_FORM_STEPS[formStepName].stepId, formValues.payment_method);
    }
  }

  componentWillUnmount() {
    document.body.classList.remove('donation-page-opened');
  }

  /**
   * Update page URL based on current donation form step.
   */
  onStepChange({ name: stepName, index: stepIndex }) {
    // Set URL of the new step.
    const { router, pathPrefix, formDonationType } = this.props;
    if (DONATION_FORM_STEPS[stepName] === undefined) {
      return;
    }

    const stepPath = buildStepPath(pathPrefix, stepName, { type: formDonationType });
    // Sends checkout step data to GTM.
    this.sendGTMCheckoutStep(DONATION_FORM_STEPS[stepName].stepId);

    // Removes query params from path to compare with stepPath.
    let rawAsPath = router.asPath;
    if (rawAsPath.includes('?')) {
      rawAsPath = rawAsPath.split('?')[0];
    }

    // Removes query params from step path.
    let rawStepPath = stepPath;
    if (rawStepPath.includes('?')) {
      rawStepPath = rawStepPath.split('?')[0];
    }

    if (rawAsPath === rawStepPath) {
      // Path is already correct.
      return;
    }

    const route = {
      pathname: router.pathname,
      query: { ...router.query, type: formDonationType, stepName, stepIndex },
    };

    // Replace is used to avoid adding each step to the browser history.
    // Restoring the step of the form from URL is not supported (at least now).
    debug('Replacing path in browser from %s to %s', router.asPath, stepPath);
    router
      .replace(route, stepPath, { shallow: true })
      // Shallow routing doesn't redirect to the top of the page.
      // Consider moving this code to onRouteChangeComplete event handler.
      .then(() => window.scrollTo(0, 0));
  }

  /**
   * Determine default donation type based on the appeal and current URL.
   */
  getDefaultType(stepname) {
    const {
      pathPrefix,
      pathSuffix,
      formConfig: { supportsSingle, supportsRecurring },
    } = this.props;

    const currentPath = `${pathPrefix}${pathSuffix}`;

    // Both types are supported by this appeal. Try to determine type from URL.
    if (supportsSingle && supportsRecurring) {
      const variants = [
        { type: RG, path: buildStepPath(pathPrefix, stepname, { type: RG }) },
        { type: SD, path: buildStepPath(pathPrefix, stepname, { type: SD }) },
      ];

      for (let i = 0; i < variants.length; i += 1) {
        // For amount step we define donation type by path
        // For example: in case of the URL /donate/my-appeal/single?type=single
        // we check that the URL starts with /donate/my-appeal/single.
        // We ignore query, because users can visit URL without query parameter
        // for example when they visit just /donate/my-appeal/single URL.
        if (stepname === 'amount' && currentPath.startsWith(variants[i].path.split('?')[0])) {
          return variants[i].type;
        }

        // For all other steps we check if current PATH matches built
        // path variant. For example /donate/my-appeal/address?type=single
        // Because built variant contains type in query.
        if (stepname !== 'amount' && currentPath.startsWith(variants[i].path)) {
          return variants[i].type;
        }
      }
      // Fallback to SD if both types are supported.
      return SD;
    }

    // Only one type supported. Simply returning it.
    if (supportsRecurring) {
      return RG;
    }
    return SD;
  }

  /**
   * Prepare steps names for donation form.
   */
  getEnabledStepNames() {
    const { router } = this.props;
    // Thank You is a special step (rendered separately).
    const { 'thank-you': excluded, ...steps } = DONATION_FORM_STEPS; // eslint-disable-line no-unused-vars

    // Exclude Address step if dsn and valid email is passed in GET params.
    if (hasShortformValuesInQuery(router.query)) {
      delete steps.address;
      // Exclude pseudo-contact preferences step if opt-in isn't explicitly set.
      if (parseInt(router.query.optin, 10) !== 1) {
        delete steps.preferences;
      }
    }

    return Object.keys(steps);
  }

  /**
   * Sends checkout step data to GTM.
   */
  sendGTMCheckoutStep(step) {
    const { formValues = {}, formConfig, paymentMethod } = this.props;

    if (!formValues) {
      return;
    }
    const products = [
      {
        name: formConfig.title,
        id: formValues.appealId,
        price: formValues.amount ? formValues.amount : '',
        category: 'Appeal',
        variant: formValues.type,
        quantity: 1,
      },
    ];
    gtmPushCheckoutStep(step, paymentMethod, products);
  }

  /**
   * Update page URL to Thank You page.
   * Note that component is not responsible for Thank You page rendering.
   */
  redirectToThankYouPage() {
    const { orderID = 0, pathPrefix, router, formValues = {} } = this.props;
    const thankYouPage = buildStepPath(pathPrefix, 'thank-you', { orderID, type: formValues.type });
    // Store donation details to display them on the 'Thank you' page.
    // Few body blocks use the data.
    sessionStorageSetItem(
      'donationDetails',
      JSON.stringify({
        name: formValues.first_name,
        orderId: orderID,
        totalAmount: formValues.fundraisingCostsApplied
          ? formValues.totalAmount
          : formValues.amount,
      }),
    );
    const route = {
      pathname: router.pathname,
      query: Object.assign(router.query, { orderID }),
    };
    router
      .push(route, thankYouPage, { shallow: true })
      // Shallow routing doesn't redirect to the top of the page.
      // Consider moving this code to onRouteChangeComplete event handler.
      .then(() => window.scrollTo(0, 0));
  }

  render() {
    const {
      formConfig,
      homepageLink,
      showRevolutStripe = false,
      pageTitle,
      router,
      isRecaptchaEnabled,
      emailOptinDescription = '',
      footerSettings = {
        columnOneHeader: '',
        columnOneContent: '',
        columnTwoHeader: '',
        columnTwoContent: '',
        text: '',
        images: [],
        googleTranslateEnabled: false,
      },
      nonce,
    } = this.props;
    const stepNames = this.getEnabledStepNames();
    const currentStepName = router.query.stepName ? router.query.stepName : stepNames[0];
    const defaultDonationType = this.getDefaultType(currentStepName);

    return (
      <div
        id="donation-form-page"
        className={`donation-form-page current-step-${currentStepName} donation-form-region-${DEFAULT_COUNTRY_CODE.toLowerCase()}`}
      >
        <Head>
          <title>{`Donate - ${pageTitle}`}</title>
        </Head>
        <Header
          display="basic"
          tags={formConfig.headerTags}
          homeNextLink={homepageLink}
          isCheckoutHeader
        />
        <div
          className={`donation-form-form ${heroStyles['appeal-hero-with-money-handles']} ${heroStyles[`appeal-hero-with-money-handles--${formConfig.styling}`]}`}
        >
          <HeroImageWithShapedBackground
            image={{ large: formConfig.image }}
            showDec={formConfig.styling === 'dec'}
          />

          <div className="container">
            <DonationForm
              key={formConfig.appealId}
              {...formConfig}
              defaultDonationType={defaultDonationType}
              defaultAmount={router.query.amount ? parseInt(router.query.amount, 10) : 0}
              enabledSteps={stepNames}
              emailOptinDescription={emailOptinDescription}
              showRevolutStripe={showRevolutStripe}
              isRecaptchaEnabled={isRecaptchaEnabled}
              nonce={nonce}
            />
          </div>
        </div>

        <div className="donation-form-footer">
          <DonationFormFooter {...footerSettings} />
        </div>
      </div>
    );
  }
}

DonationFormPage.propTypes = {
  pathPrefix: PropTypes.string.isRequired,
  pathSuffix: PropTypes.string.isRequired,
  formConfig: PropTypes.shape(DonationForm.propTypes).isRequired,
  formValues: PropTypes.shape(),
  router: PropTypes.shape().isRequired,
  completed: PropTypes.bool,
  orderID: PropTypes.number,
  formDonationType: PropTypes.string.isRequired,
  homepageLink: PropTypes.shape().isRequired,
  showRevolutStripe: PropTypes.bool,
  isRecaptchaEnabled: PropTypes.bool.isRequired,
  pageTitle: PropTypes.string.isRequired,
  footerSettings: PropTypes.shape({
    columnOneHeader: PropTypes.string,
    columnOneContent: PropTypes.string,
    columnTwoHeader: PropTypes.string,
    columnTwoContent: PropTypes.string,
    text: PropTypes.string,
    images: PropTypes.arrayOf(
      PropTypes.shape({
        url: PropTypes.string,
        alt: PropTypes.string,
      }),
    ),
    googleTranslateEnabled: PropTypes.bool,
  }),
  paymentMethod: PropTypes.string.isRequired,
  formStepName: PropTypes.string.isRequired,
  formStepIndex: PropTypes.number.isRequired,
  emailOptinDescription: PropTypes.string,
  nonce: PropTypes.string,
};

const mapStateToProps = ({ donationForm, globalSettings }) => ({
  completed: !donationForm.form.submitting && donationForm.paymentCompleted,
  orderID: donationForm.orderID,
  homepageLink: globalSettings.headerSettings.homeNextLink,
  showRevolutStripe: globalSettings.generalSettings.showRevolutStripe,
  isRecaptchaEnabled:
    globalSettings.generalSettings.showGoogleAppleCaptcha ||
    globalSettings.generalSettings.showCreditCardCaptcha,
  footerSettings: getDonationFormFooterSettingsSelector(globalSettings),
  emailOptinDescription: globalSettings.generalSettings.emailOptinDescription,
  // Subscribe to necessary fields from donation form values object.
  formDonationType: donationForm.form.values.type || '',
  formValues: donationForm.form.values,
  paymentMethod: donationForm.form.values.payment_method || '',
  formStepName: donationForm.form.values.step ? donationForm.form.values.step.name : 'amount',
  formStepIndex: donationForm.form.values.step ? donationForm.form.values.step.index : 0,
});

export default connect(mapStateToProps)(withRouter(DonationFormPage));
